import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, withKeys as _withKeys, renderList as _renderList, Fragment as _Fragment, vShow as _vShow, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-05152d8f"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  id: "edit-datastore-table",
  class: "datastore-editor"
}
const _hoisted_2 = { class: "datastore-center" }
const _hoisted_3 = { class: "datastore-toolbar" }
const _hoisted_4 = { class: "datastore-title" }
const _hoisted_5 = { class: "datastore-meta" }
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "flex-grow-1" }, null, -1))
const _hoisted_7 = { class: "d-flex align-items-center" }
const _hoisted_8 = { class: "btn-label" }
const _hoisted_9 = { class: "btn-text" }
const _hoisted_10 = {
  key: 0,
  id: "datastore-quota-banner-full"
}
const _hoisted_11 = {
  key: 1,
  id: "datastore-quota-banner-warn"
}
const _hoisted_12 = { class: "datastore-grid-wrap" }
const _hoisted_13 = ["id", "aria-label", "onClick", "onKeyup"]
const _hoisted_14 = ["id", "role", "tabindex", "onClick", "onKeyup"]
const _hoisted_15 = {
  key: 3,
  class: "datastore-cell-text"
}
const _hoisted_16 = ["aria-label"]
const _hoisted_17 = { class: "datastore-foot" }
const _hoisted_18 = {
  key: 0,
  class: "datastore-grid-foot"
}
const _hoisted_19 = {
  key: 0,
  class: "datastore-foot-warn"
}
const _hoisted_20 = { class: "pagination-wrapper" }
const _hoisted_21 = { class: "right-menu-bar height-100 d-flex flex-column datastore-rail" }
const _hoisted_22 = { class: "nav flex-column nav-tabs no-border" }
const _hoisted_23 = {
  id: "datastore-rail-info",
  class: "nav-link tab-link d-flex align-items-center"
}
const _hoisted_24 = {
  id: "datastore-rail-fields",
  class: "nav-link tab-link d-flex align-items-center"
}
const _hoisted_25 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "datastore-rail-divider" }, null, -1))
const _hoisted_26 = { class: "d-flex flex-column align-items-center" }
const _hoisted_27 = ["aria-label"]
const _hoisted_28 = ["aria-label"]
const _hoisted_29 = ["aria-label"]
const _hoisted_30 = { class: "loader-div-40" }
const _hoisted_31 = ["src"]

import { computed, nextTick, onMounted, ref } from "vue";
import {
  ElButton,
  ElCheckbox,
  ElInput,
  ElTable,
  ElTableColumn,
  ElTag,
  ElTooltip,
} from "element-plus";
import { TYPE } from "vue-toastification";
import { showAlert, showConfirm } from "@/core/popup";
import i18n from "@/core/i18n/i18n";
import { router } from "@/core/router";
import { ASSETS_BASE_URL, DEFAULT_LOGO } from "@/core/logo";
import { changeListItemOfBreadcrumb } from "@/core/layout";
import OneTegPagination from "@/common/components/OneTegPagination.vue";
import FieldDesignerDrawer from "@/datastore/components/FieldDesignerDrawer.vue";
import RowDetailDrawer from "@/datastore/components/RowDetailDrawer.vue";
import TableInfoDrawer from "@/datastore/components/TableInfoDrawer.vue";
import ImportWizard from "@/datastore/components/ImportWizard.vue";
import ExportDialog from "@/datastore/components/ExportDialog.vue";
import ShareTableDialog from "@/datastore/components/ShareTableDialog.vue";
import {
  DataStoreField,
  DataStoreLimits,
  DataStoreRow,
  DataStoreTable,
  RowPage,
} from "@/datastore/types";
import {
  canDataStore,
  DEFAULT_DATASTORE_LIMITS,
  deleteRows,
  getLimits,
  getRows,
  getTable,
  saveRow,
} from "@/datastore/services/datastore.service";
import { clampPage, isPageJumpAllowed } from "@/datastore/utils/cursorPaging";
import { validateCell } from "@/datastore/utils/cellValidate";


export default /*#__PURE__*/_defineComponent({
  __name: 'EditDataStoreTable',
  setup(__props) {

const { t } = i18n.global;

// Governor limits (design §10) come from the service that enforces them, so a
// retuned tenant is reflected here without a rebuild. DEFAULT_DATASTORE_LIMITS is
// the last-resort fallback if that lookup fails, never the source of truth.
const limits = ref<DataStoreLimits>(DEFAULT_DATASTORE_LIMITS);
const rowLimit = computed(() => limits.value.maxRowsPerTable);
const maxOffset = computed(() => limits.value.maxOffset);

const tableId = String(router.currentRoute.value.params.id ?? "");
const table = ref<DataStoreTable | null>(null);
const page = ref<RowPage>({ rows: [], total: 0, offset: 0, nextCursor: null });
const currentPage = ref(1);
const pageSize = ref(100);
const rowSearch = ref("");
const loading = ref(false);
const activeDrawer = ref<"info" | "fields" | "row" | null>(null);
const selectedRow = ref<DataStoreRow | null>(null);
const grid = ref<InstanceType<typeof ElTable> | null>(null);
const selectedRows = ref<DataStoreRow[]>([]);
const deleting = ref(false);
const importVisible = ref(false);
const exportVisible = ref(false);
const shareVisible = ref(false);
const fieldDrawer = ref<InstanceType<typeof FieldDesignerDrawer> | null>(null);

const editing = ref<{ rowId: string; fieldId: string } | null>(null);
const cellDraft = ref("");

const sortedFields = computed(() =>
  [...(table.value?.fields ?? [])].sort((a, b) => a.ordinal - b.ordinal)
);
const isBigTable = computed(() => (table.value?.rowCount ?? 0) > 100_000);
const usagePercent = computed(() =>
  rowLimit.value > 0 ? ((table.value?.rowCount ?? 0) / rowLimit.value) * 100 : 0
);
/**
 * "ok" below 80%, "warn" from 80%, "full" at the ceiling. The three states are
 * design §10's own thresholds; the server refuses at the same number, so the
 * banner and the 409 always agree.
 */
const quotaState = computed<"ok" | "warn" | "full">(() => {
  if (usagePercent.value >= 100) return "full";
  return usagePercent.value >= 80 ? "warn" : "ok";
});
/**
 * The privilege says what this user may do; the grant says what their organization may do, and a
 * row write needs both. Without the grant half a read-only grantee saw a fully editable grid and
 * only learned otherwise from the 403 on save.
 */
const cellsEditable = computed(
  () =>
    canDataStore("DATA.EDIT") &&
    !isBigTable.value &&
    (table.value?.owned === true || table.value?.access !== "READ")
);
/** Structural change — the same disjunction one level up the ladder. */
const structureEditable = computed(
  () =>
    canDataStore("TABLE.EDIT") &&
    (table.value?.owned === true || table.value?.access === "MODIFY")
);

function formatCount(n: number): string {
  return n.toLocaleString("en-US");
}

/**
 * The upgrade path is a conversation, not a button that provisions a cluster:
 * enabling the Baserow tier for an organization is an administrator action, so
 * the call to action says who to ask rather than pretending to do it here.
 */
function onUpgrade() {
  showAlert(TYPE.INFO, t("DataStore.UpgradeHint"));
}

async function loadTable() {
  table.value = await getTable(tableId);
  changeListItemOfBreadcrumb({
    labelKey: table.value.name,
    route: `/datastore/edit/${tableId}`,
    disabled: true,
    isTranslated: false,
    uniqueLabel: table.value.name,
  });
}

async function loadRows() {
  if (!table.value) return;
  page.value = await getRows(tableId, {
    page: currentPage.value,
    pageSize: pageSize.value,
    search: rowSearch.value || undefined,
  });
  // A selection only ever means the rows the user can currently see, so it does
  // not survive a page, a search or a reload — carrying it would let a delete
  // reach rows that scrolled out of view.
  clearRowSelection();
}

async function reloadAll() {
  await loadTable();
  await loadRows();
}

function onSearchChanged() {
  currentPage.value = 1;
  loadRows();
}

function onPageChange(newPage: number) {
  // Offset jumps are capped (datastore.limits.max-offset); past the cap the real API
  // requires cursor walking — the prototype surfaces the same rule.
  if (!isPageJumpAllowed(newPage, pageSize.value, maxOffset.value)) {
    showAlert(TYPE.WARNING, t("DataStore.OffsetCapWarning"));
  }
  currentPage.value = clampPage(newPage, page.value.total, pageSize.value);
  loadRows();
}

/**
 * OneTegPagination binds `:page-size` one-way onto ElPagination, so its
 * `update:pageSize` never fires — the new size only arrives on `size-change`.
 */
function onPageSizeChange(newSize: number) {
  pageSize.value = newSize;
  currentPage.value = 1;
  loadRows();
}

/* -------- inline cell editing -------- */

function isEditing(rowId: string, fieldId: string): boolean {
  return editing.value?.rowId === rowId && editing.value?.fieldId === fieldId;
}

function onCellDblClick(row: DataStoreRow, column: { property?: string }) {
  if (!cellsEditable.value || !column.property) return;
  const field = table.value?.fields.find((f) => f.id === column.property);
  if (!field || field.type === "BOOLEAN") return;
  editing.value = { rowId: row.id, fieldId: field.id };
  cellDraft.value = String(row.cells[field.id] ?? "");
}

async function commitCell(row: DataStoreRow, field: DataStoreField) {
  if (!isEditing(row.id, field.id)) return;
  const result = validateCell(cellDraft.value, field);
  if (!result.ok) {
    showAlert(TYPE.ERROR, result.error ?? "");
    return;
  }
  editing.value = null;
  if ((result.value ?? null) === (row.cells[field.id] ?? null)) return;
  try {
    await saveRow(tableId, row.id, {
      ...row.cells,
      [field.id]: result.value ?? null,
    });
    showAlert(TYPE.SUCCESS, t("DataStore.CellSaved"));
    await loadRows();
  } catch (e: any) {
    showAlert(TYPE.ERROR, e.message ?? t("Common.UnknownError"));
    await loadRows();
  }
}

function cancelCell() {
  editing.value = null;
}

async function toggleBoolean(
  row: DataStoreRow,
  field: DataStoreField,
  value: boolean
) {
  try {
    await saveRow(tableId, row.id, { ...row.cells, [field.id]: value });
    await loadRows();
  } catch (e: any) {
    showAlert(TYPE.ERROR, e.message ?? t("Common.UnknownError"));
  }
}

/* -------- deleting rows -------- */

function onSelectionChange(rows: DataStoreRow[]) {
  selectedRows.value = rows;
}

function clearRowSelection() {
  selectedRows.value = [];
  if (grid.value) {
    grid.value.clearSelection();
  }
}

/**
 * The catalogue carries no plural forms, so a count of one takes the singular
 * key rather than interpolating "1" into a sentence written for many.
 */
function rowsMessage(count: number, one: string, many: string): string {
  return count === 1 ? t(one) : t(many, { count: formatCount(count) });
}

/**
 * Deletes the checked rows — one row or a whole page of search hits, it is the
 * same operation. The confirmation names the exact count because the delete is a
 * hard one (the API frees the quota immediately, design §11.1), and the count
 * reported afterwards is the service's, not the size of the selection: an id
 * that is already gone deletes nothing.
 */
async function onDeleteSelectedRows() {
  const rows = [...selectedRows.value];
  if (rows.length === 0 || deleting.value) return;
  const confirmed = await showConfirm(
    "warning",
    rowsMessage(rows.length, "DataStore.DeleteRow", "DataStore.DeleteRows"),
    rowsMessage(
      rows.length,
      "DataStore.DeleteRowWarning",
      "DataStore.DeleteRowsWarning"
    )
  );
  if (!confirmed) return;

  deleting.value = true;
  showAlert(
    TYPE.INFO,
    rowsMessage(rows.length, "DataStore.DeletingRow", "DataStore.DeletingRows")
  );
  try {
    const deleted = await deleteRows(
      tableId,
      rows.map((row) => row.id)
    );
    showAlert(
      TYPE.SUCCESS,
      rowsMessage(deleted, "DataStore.RowDeleted", "DataStore.RowsDeleted")
    );
  } catch (e: any) {
    showAlert(TYPE.ERROR, e.message ?? t("Common.UnknownError"));
  } finally {
    deleting.value = false;
    // Reloaded on failure too: a batch that failed part-way through still
    // deleted the batches before it, and the grid must not claim otherwise.
    await refreshAfterDelete();
  }
}

async function refreshAfterDelete() {
  try {
    await reloadAll();
    // Deleting the last rows of a page leaves it empty; step back instead of
    // making the user page out of a blank grid.
    if (page.value.rows.length === 0 && currentPage.value > 1) {
      currentPage.value = clampPage(
        currentPage.value - 1,
        page.value.total,
        pageSize.value
      );
      await loadRows();
    }
  } catch (e: any) {
    // Saying the reload failed is the honest outcome; leaving deleted rows on
    // screen without a word is not.
    showAlert(TYPE.ERROR, e.message ?? t("Common.UnknownError"));
  }
}

/* -------- drawers -------- */

function toggleDrawer(name: "info" | "fields") {
  activeDrawer.value = activeDrawer.value === name ? null : name;
}

function openRowDrawer(row: DataStoreRow) {
  selectedRow.value = row;
  activeDrawer.value = "row";
}

function openNewRowDrawer() {
  selectedRow.value = null;
  activeDrawer.value = "row";
}

async function openFieldEditor(field: DataStoreField | null) {
  if (!structureEditable.value) return;
  activeDrawer.value = "fields";
  // wait for the drawer to mount, then jump straight into the editor
  await nextTick();
  if (fieldDrawer.value) {
    fieldDrawer.value.openEditor(field);
  }
}

onMounted(async () => {
  loading.value = true;
  try {
    limits.value = await getLimits();
    await reloadAll();
    // Arriving from "Create and import CSV" on the list page: the table exists but has only its
    // primary field, so the wizard is the next thing the user wants, not something to go find.
    if (
      router.currentRoute.value.query.import === "1" &&
      canDataStore("IMPORT")
    ) {
      importVisible.value = true;
    }
  } catch (e: any) {
    showAlert(TYPE.ERROR, e.message ?? t("Common.UnknownError"));
    router.push("/datastore");
  } finally {
    loading.value = false;
  }
});

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (table.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createVNode(_unref(ElTooltip), {
                effect: "custom",
                content: _unref(t)('Common.Back'),
                placement: "bottom"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(ElButton), {
                    id: "datastore-back-button",
                    class: "no-border btn-oneTeg",
                    size: "mini",
                    type: "primary",
                    "aria-label": _unref(t)('Common.Back'),
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).push('/datastore')))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_font_awesome_icon, { icon: "arrow-left" })
                    ]),
                    _: 1
                  }, 8, ["aria-label"])
                ]),
                _: 1
              }, 8, ["content"]),
              _createElementVNode("span", _hoisted_4, _toDisplayString(table.value.name), 1),
              (table.value.sharedWith.length)
                ? (_openBlock(), _createBlock(_unref(ElTag), {
                    key: 0,
                    size: "small",
                    type: "success"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)("DataStore.Shared")), 1)
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              _createElementVNode("span", _hoisted_5, _toDisplayString(table.value.fields.length) + " " + _toDisplayString(_unref(t)("DataStore.Fields").toLowerCase()) + " · " + _toDisplayString(formatCount(table.value.rowCount)) + " " + _toDisplayString(_unref(t)("DataStore.Rows").toLowerCase()), 1),
              _hoisted_6,
              _createVNode(_unref(ElInput), {
                id: "datastore-row-search",
                modelValue: rowSearch.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((rowSearch).value = $event)),
                size: "mini",
                placeholder: _unref(t)('DataStore.SearchRows'),
                clearable: "",
                class: "oneTeg-search-input max-w-170",
                onInput: onSearchChanged
              }, null, 8, ["modelValue", "placeholder"]),
              (cellsEditable.value && selectedRows.value.length > 0)
                ? (_openBlock(), _createBlock(_unref(ElButton), {
                    key: 1,
                    id: "datastore-delete-rows-button",
                    size: "mini",
                    type: "danger",
                    plain: "",
                    loading: deleting.value,
                    onClick: onDeleteSelectedRows
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)("DataStore.DeleteSelected", {
                count: formatCount(selectedRows.value.length),
              })), 1)
                    ]),
                    _: 1
                  }, 8, ["loading"]))
                : _createCommentVNode("", true),
              (cellsEditable.value && !isBigTable.value)
                ? (_openBlock(), _createBlock(_unref(ElButton), {
                    key: 2,
                    id: "datastore-add-row-button",
                    class: "no-border btn-oneTeg2-table-create btn-labeled",
                    size: "mini",
                    type: "primary",
                    onClick: openNewRowDrawer
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("span", _hoisted_7, [
                        _createElementVNode("span", _hoisted_8, [
                          _createVNode(_component_font_awesome_icon, { icon: "plus" })
                        ]),
                        _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(t)("DataStore.NewRow")), 1)
                      ])
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]),
            (quotaState.value !== 'ok')
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  id: "datastore-quota-banner",
                  class: _normalizeClass(["datastore-quota-banner", quotaState.value])
                }, [
                  _createVNode(_component_font_awesome_icon, {
                    icon: "info-circle",
                    class: _normalizeClass(quotaState.value === 'full' ? 'color-red' : 'color-warning')
                  }, null, 8, ["class"]),
                  (quotaState.value === 'full')
                    ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(_unref(t)("DataStore.QuotaFull", {
                limit: formatCount(rowLimit.value),
              })), 1))
                    : (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(_unref(t)("DataStore.QuotaNearingLimit", {
                used: formatCount(table.value.rowCount),
                limit: formatCount(rowLimit.value),
                percent: usagePercent.value.toFixed(0),
              })), 1)),
                  (quotaState.value === 'full')
                    ? (_openBlock(), _createBlock(_unref(ElButton), {
                        key: 2,
                        id: "datastore-quota-upgrade",
                        class: "btn-oneTeg2",
                        size: "mini",
                        onClick: onUpgrade
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(t)("DataStore.UpgradeToDataStorePlus")), 1)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ], 2))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_12, [
              _createVNode(_unref(ElTable), {
                ref_key: "grid",
                ref: grid,
                data: page.value.rows,
                border: "",
                height: "100%",
                class: "oneTeg-table oneTeg-table--sm width-100",
                "row-key": "id",
                "empty-text": loading.value ? ' ' : _unref(t)('Common.NoData'),
                onCellDblclick: onCellDblClick,
                onSelectionChange: onSelectionChange
              }, {
                default: _withCtx(() => [
                  (cellsEditable.value)
                    ? (_openBlock(), _createBlock(_unref(ElTableColumn), {
                        key: 0,
                        type: "selection",
                        width: "55",
                        fixed: ""
                      }))
                    : _createCommentVNode("", true),
                  _createVNode(_unref(ElTableColumn), {
                    width: "70",
                    align: "right",
                    fixed: ""
                  }, {
                    header: _withCtx(() => [
                      _createTextVNode("#")
                    ]),
                    default: _withCtx((scope) => [
                      _createVNode(_unref(ElTooltip), {
                        effect: "custom",
                        content: _unref(t)('DataStore.OpenRow'),
                        placement: "right"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("a", {
                            id: 'datastore-rownum-' + scope.$index,
                            class: "datastore-rownum",
                            role: "button",
                            tabindex: "0",
                            "aria-label": _unref(t)('DataStore.OpenRow'),
                            onClick: ($event: any) => (openRowDrawer(scope.row)),
                            onKeyup: _withKeys(($event: any) => (openRowDrawer(scope.row)), ["enter"])
                          }, _toDisplayString(formatCount(page.value.offset + scope.$index + 1)), 41, _hoisted_13)
                        ]),
                        _: 2
                      }, 1032, ["content"])
                    ]),
                    _: 1
                  }),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedFields.value, (field) => {
                    return (_openBlock(), _createBlock(_unref(ElTableColumn), {
                      key: field.id,
                      prop: field.id,
                      "min-width": "150"
                    }, {
                      header: _withCtx(() => [
                        _createVNode(_unref(ElTooltip), {
                          effect: "custom",
                          content: _unref(t)('DataStore.EditField'),
                          disabled: !structureEditable.value,
                          placement: "bottom"
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("span", {
                              id: 'datastore-col-' + field.id,
                              class: _normalizeClass(["datastore-col-head", { editable: structureEditable.value }]),
                              role: structureEditable.value ? 'button' : undefined,
                              tabindex: structureEditable.value ? 0 : undefined,
                              onClick: ($event: any) => (openFieldEditor(field)),
                              onKeyup: _withKeys(($event: any) => (openFieldEditor(field)), ["enter"])
                            }, [
                              _createTextVNode(_toDisplayString(field.name) + " ", 1),
                              (field.isPrimary)
                                ? (_openBlock(), _createBlock(_component_font_awesome_icon, {
                                    key: 0,
                                    icon: "key",
                                    class: "datastore-col-badge"
                                  }))
                                : (field.uniqueKey)
                                  ? (_openBlock(), _createBlock(_component_font_awesome_icon, {
                                      key: 1,
                                      icon: "fingerprint",
                                      class: "datastore-col-badge"
                                    }))
                                  : _createCommentVNode("", true)
                            ], 42, _hoisted_14)
                          ]),
                          _: 2
                        }, 1032, ["content", "disabled"])
                      ]),
                      default: _withCtx((scope) => [
                        (isEditing(scope.row.id, field.id))
                          ? (_openBlock(), _createBlock(_unref(ElInput), {
                              key: 0,
                              id: 'datastore-cell-input',
                              modelValue: cellDraft.value,
                              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((cellDraft).value = $event)),
                              size: "mini",
                              onKeyup: [
                                _withKeys(($event: any) => (commitCell(scope.row, field)), ["enter"]),
                                _withKeys(cancelCell, ["esc"])
                              ],
                              onBlur: ($event: any) => (commitCell(scope.row, field))
                            }, null, 8, ["modelValue", "onKeyup", "onBlur"]))
                          : (field.type === 'BOOLEAN')
                            ? (_openBlock(), _createBlock(_unref(ElCheckbox), {
                                key: 1,
                                "model-value": scope.row.cells[field.id] === true,
                                disabled: !cellsEditable.value,
                                onChange: (v) => toggleBoolean(scope.row, field, v)
                              }, null, 8, ["model-value", "disabled", "onChange"]))
                            : (
                    field.type === 'SINGLE_SELECT' && scope.row.cells[field.id]
                  )
                              ? (_openBlock(), _createBlock(_unref(ElTag), {
                                  key: 2,
                                  size: "small"
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(scope.row.cells[field.id]), 1)
                                  ]),
                                  _: 2
                                }, 1024))
                              : (_openBlock(), _createElementBlock("span", _hoisted_15, _toDisplayString(scope.row.cells[field.id]), 1))
                      ]),
                      _: 2
                    }, 1032, ["prop"]))
                  }), 128)),
                  (structureEditable.value)
                    ? (_openBlock(), _createBlock(_unref(ElTableColumn), {
                        key: 1,
                        width: "80"
                      }, {
                        header: _withCtx(() => [
                          _createVNode(_unref(ElTooltip), {
                            effect: "custom",
                            content: _unref(t)('DataStore.AddField'),
                            placement: "bottom"
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("a", {
                                id: "datastore-add-field-header",
                                class: "datastore-add-field",
                                role: "button",
                                tabindex: "0",
                                "aria-label": _unref(t)('DataStore.AddField'),
                                onClick: _cache[3] || (_cache[3] = ($event: any) => (openFieldEditor(null))),
                                onKeyup: _cache[4] || (_cache[4] = _withKeys(($event: any) => (openFieldEditor(null)), ["enter"]))
                              }, [
                                _createVNode(_component_font_awesome_icon, { icon: "plus" })
                              ], 40, _hoisted_16)
                            ]),
                            _: 1
                          }, 8, ["content"])
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }, 8, ["data", "empty-text"])
            ]),
            _createElementVNode("div", _hoisted_17, [
              (isBigTable.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                    (isBigTable.value)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_19, [
                          _createVNode(_component_font_awesome_icon, {
                            icon: "info-circle",
                            class: "color-warning margin-right-10"
                          }),
                          _createTextVNode(_toDisplayString(_unref(t)("DataStore.NonIndexedSortWarning")), 1)
                        ]))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_20, [
                _createVNode(OneTegPagination, {
                  total: page.value.total,
                  "current-page": currentPage.value,
                  "page-size": pageSize.value,
                  "page-sizes": [50, 100, 200],
                  background: true,
                  small: true,
                  layout: 'total, sizes, prev, pager, next',
                  onCurrentChange: onPageChange,
                  onSizeChange: onPageSizeChange
                }, null, 8, ["total", "current-page", "page-size"])
              ])
            ])
          ]),
          _createElementVNode("div", {
            class: _normalizeClass(["datastore-drawer-slot", { open: activeDrawer.value !== null }])
          }, [
            (activeDrawer.value === 'fields')
              ? (_openBlock(), _createBlock(FieldDesignerDrawer, {
                  key: 0,
                  ref_key: "fieldDrawer",
                  ref: fieldDrawer,
                  table: table.value,
                  onClose: _cache[5] || (_cache[5] = ($event: any) => (activeDrawer.value = null)),
                  onChanged: reloadAll
                }, null, 8, ["table"]))
              : (activeDrawer.value === 'row')
                ? (_openBlock(), _createBlock(RowDetailDrawer, {
                    key: 1,
                    table: table.value,
                    row: selectedRow.value,
                    readonly: isBigTable.value,
                    onClose: _cache[6] || (_cache[6] = ($event: any) => (activeDrawer.value = null)),
                    onChanged: reloadAll
                  }, null, 8, ["table", "row", "readonly"]))
                : (activeDrawer.value === 'info')
                  ? (_openBlock(), _createBlock(TableInfoDrawer, {
                      key: 2,
                      table: table.value,
                      "row-limit": rowLimit.value,
                      onClose: _cache[7] || (_cache[7] = ($event: any) => (activeDrawer.value = null)),
                      onChanged: reloadAll
                    }, null, 8, ["table", "row-limit"]))
                  : _createCommentVNode("", true)
          ], 2),
          _createElementVNode("div", _hoisted_21, [
            _createElementVNode("ul", _hoisted_22, [
              _createElementVNode("li", {
                class: _normalizeClass(["nav-item-border width-100", { 'active-flow-tab': activeDrawer.value === 'info' }]),
                onClick: _cache[8] || (_cache[8] = ($event: any) => (toggleDrawer('info')))
              }, [
                _createVNode(_unref(ElTooltip), {
                  effect: "custom",
                  content: _unref(t)('DataStore.TableDetails'),
                  placement: "left"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("a", _hoisted_23, [
                      _createVNode(_component_font_awesome_icon, {
                        icon: "info",
                        class: "fa-fw",
                        size: "lg"
                      })
                    ])
                  ]),
                  _: 1
                }, 8, ["content"])
              ], 2),
              _createElementVNode("li", {
                class: _normalizeClass(["nav-item-border width-100", { 'active-flow-tab': activeDrawer.value === 'fields' }]),
                onClick: _cache[9] || (_cache[9] = ($event: any) => (toggleDrawer('fields')))
              }, [
                _createVNode(_unref(ElTooltip), {
                  effect: "custom",
                  content: _unref(t)('DataStore.Fields'),
                  placement: "left"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("a", _hoisted_24, [
                      _createVNode(_component_font_awesome_icon, {
                        icon: "table",
                        class: "fa-fw",
                        size: "lg"
                      })
                    ])
                  ]),
                  _: 1
                }, 8, ["content"])
              ], 2)
            ]),
            _hoisted_25,
            _createElementVNode("div", _hoisted_26, [
              (_unref(canDataStore)('IMPORT') && _unref(canDataStore)('DATA.EDIT'))
                ? (_openBlock(), _createBlock(_unref(ElTooltip), {
                    key: 0,
                    effect: "custom",
                    content: _unref(t)('DataStore.Import'),
                    placement: "left"
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("button", {
                        id: "datastore-rail-import",
                        type: "button",
                        class: "btn btn-link",
                        "aria-label": _unref(t)('DataStore.Import'),
                        onClick: _cache[10] || (_cache[10] = ($event: any) => (importVisible.value = true))
                      }, [
                        _createVNode(_component_font_awesome_icon, {
                          icon: "upload",
                          class: "fa-fw"
                        })
                      ], 8, _hoisted_27)
                    ]),
                    _: 1
                  }, 8, ["content"]))
                : _createCommentVNode("", true),
              (_unref(canDataStore)('EXPORT') && _unref(canDataStore)('DATA.VIEW'))
                ? (_openBlock(), _createBlock(_unref(ElTooltip), {
                    key: 1,
                    effect: "custom",
                    content: _unref(t)('DataStore.Export'),
                    placement: "left"
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("button", {
                        id: "datastore-rail-export",
                        type: "button",
                        class: "btn btn-link",
                        "aria-label": _unref(t)('DataStore.Export'),
                        onClick: _cache[11] || (_cache[11] = ($event: any) => (exportVisible.value = true))
                      }, [
                        _createVNode(_component_font_awesome_icon, {
                          icon: "download",
                          class: "fa-fw"
                        })
                      ], 8, _hoisted_28)
                    ]),
                    _: 1
                  }, 8, ["content"]))
                : _createCommentVNode("", true),
              (_unref(canDataStore)('SHARE') && table.value.owned)
                ? (_openBlock(), _createBlock(_unref(ElTooltip), {
                    key: 2,
                    effect: "custom",
                    content: _unref(t)('DataStore.Share'),
                    placement: "left"
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("button", {
                        id: "datastore-rail-share",
                        type: "button",
                        class: "btn btn-link",
                        "aria-label": _unref(t)('DataStore.Share'),
                        onClick: _cache[12] || (_cache[12] = ($event: any) => (shareVisible.value = true))
                      }, [
                        _createVNode(_component_font_awesome_icon, {
                          icon: "share-square",
                          class: "fa-fw"
                        })
                      ], 8, _hoisted_29)
                    ]),
                    _: 1
                  }, 8, ["content"]))
                : _createCommentVNode("", true)
            ])
          ]),
          _createVNode(ImportWizard, {
            modelValue: importVisible.value,
            "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((importVisible).value = $event)),
            table: table.value,
            onChanged: reloadAll
          }, null, 8, ["modelValue", "table"]),
          _createVNode(ExportDialog, {
            modelValue: exportVisible.value,
            "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((exportVisible).value = $event)),
            table: table.value
          }, null, 8, ["modelValue", "table"]),
          _createVNode(ShareTableDialog, {
            modelValue: shareVisible.value,
            "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((shareVisible).value = $event)),
            table: table.value,
            onChanged: reloadAll
          }, null, 8, ["modelValue", "table"])
        ], 64))
      : _createCommentVNode("", true),
    _withDirectives(_createElementVNode("div", _hoisted_30, [
      _createElementVNode("img", {
        src: `${_unref(ASSETS_BASE_URL)}/${_unref(DEFAULT_LOGO)}.png`,
        alt: "Loading...",
        class: "loader-logo-40"
      }, null, 8, _hoisted_31)
    ], 512), [
      [_vShow, loading.value]
    ])
  ]))
}
}

})